
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { IdCardsHeader } from '@/components/dashboard/id-cards/IdCardsHeader';
import { IdCardsList } from '@/components/dashboard/id-cards/IdCardsList';
import { IdCardsPromotional } from '@/components/dashboard/id-cards/IdCardsPromotional';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useAuthState } from '@/hooks/useAuthState';

type IdCard = {
  id: string;
  user_id: string;
  file_path: string;
  uploaded_at: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
};

export default function IdCardsPage() {
  const { userEmail, loading: authLoading } = useAuthState();
  const { toast } = useToast();
  const { role } = useUserRoles(null);

  const [idCards, setIdCards] = useState<IdCard[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchIdCards = async () => {
    if (!userEmail) return [];
    const { data, error } = await supabase
      .from('id_cards')
      .select('*')
      .eq('uploaded_by', userEmail);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not fetch ID cards'
      });
      return [];
    }
    return data as IdCard[];
  };

  const { data: queryData, isLoading: isQueryLoading } = useQuery({
    queryKey: ['idCards', userEmail],
    queryFn: fetchIdCards,
    enabled: !!userEmail
  });

  useEffect(() => {
    if (queryData) {
      setIdCards(queryData);
      setLoading(false);
    }
  }, [queryData]);

  if (authLoading) return null;

  return (
    <div className="container mx-auto px-4 py-6">
      <IdCardsHeader 
        title="My ID Cards" 
        isAdmin={role === 'admin'} 
      />
      
      {loading || isQueryLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : idCards.length > 0 ? (
        <IdCardsList idCards={idCards} />
      ) : (
        <IdCardsPromotional />
      )}
    </div>
  );
}
